import React, { memo } from "react";
import PropTypes from "prop-types";
import { CircularProgress } from "@mui/material";
import Grid from "@mui/material/Grid2";
import makeStyles from '@mui/styles/makeStyles';
import { COLORS } from "../../../constants/colors";
import { useTranslation } from "react-i18next";
import localizationKeys from "../../../i18n/localizationKeys";
import { Title } from "../title";
import { useSelector } from "react-redux";
import { isChannelModFetching } from "../../../model/slice/channelSlice";

const useStyles = makeStyles(() => ({
  container: {
    height: "100%",
    width: "100%",
  },
  time: {
    height: "40px",
  },
  left: {
    // margin: 'auto',
  },
  right: {
    // paddingLeft: "20px",
  },
  table: {
    "& thead tr td": {
      fontSize: "12px",
      color: COLORS.white,
      height: "18px",
    },
    "& tbody tr": {
      fontSize: "12px",
      height: "20px",
    },
    "& tbody tr td:first-child": {
      color: COLORS.white,
      width: "200px",
      whiteSpace: "nowrap",
    },
    "& tbody tr td:not(:first-child)": {
      color: COLORS.greenishBlue,
      width: "80px",
    },
  },
}));

export const Details = memo((props) => {
  const { data, isVesselOverview } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const isLoading = useSelector(isChannelModFetching);
  const pipeArea = Math.PI * (199 / 2) ** 2;
  const flowRate = data.find((value) => value.tag === "O9");
  const flowSpeed = ((10000 * flowRate?.inputData) / (36 * pipeArea)).toFixed(1);
  const density = data.find((value) => value.tag === "O10");
  const outsideTemp = data.find((value) => value.tag === "T51");
  const No2tempOfInner = data.find((value) => value.tag === "T29");
  const No2tempOfOuter = data.find((value) => value.tag === "T26");
  const No2tankCoverInnerTemp = data.find((value) => value.tag === "T31");
  const No2bottomFillingLowerTemp = data.find((value) => value.tag === "T7");
  const No2cargoTankPress = data.find((value) => value.tag === "P47");
  const No2cargoTankTemp100 = data.find((value) => value.tag === "T32");
  const No2cargoTankTemp50 = data.find((value) => value.tag === "T33");
  const No2cargoTankTemp40 = data.find((value) => value.tag === "T34");
  const No2cargoTankTemp25 = data.find((value) => value.tag === "T35");
  const No2cargoPumpSuctionTemp = data.find((value) => value.tag === 'T9');
  const No2press1 = data.find((value) => value.tag === "P1");
  const No2press2 = data.find((value) => value.tag === "P2");
  const No2press5 = data.find((value) => value.tag === "P5");
  const No2press6 = data.find((value) => value.tag === "P6");
  const No2press8 = data.find((value) => value.tag === "P8");
  const No1cargoTankPress = data.find((value) => value.tag === "P46");
  const No1bottomFillingLowerTemp = data.find((value) => value.tag === "T65");
  const No1cargoPumpSuctionTemp = data.find((value) => value.tag === 'T66');
  const No1cargoTankTemp100 = data.find((value) => value.tag === "T61");
  const No1cargoTankTemp50 = data.find((value) => value.tag === "T62");
  const No1cargoTankTemp40 = data.find((value) => value.tag === "T63");
  const No1cargoTankTemp25 = data.find((value) => value.tag === "T64");
  const pressureLossUnloading1 = (Number(No2press8?.inputData) - Number(No2press1?.inputData)).toFixed(3);
  const pressureLossUnloading2 = (Number(No2press8?.inputData) - Number(No2press2?.inputData)).toFixed(3);
  const pressureLossLoading1 = (Number(No2press1?.inputData) - Number(No2press5?.inputData)).toFixed(3);
  const pressureLossLoading2 = (Number(No2press2?.inputData) - Number(No2press5?.inputData)).toFixed(3);
  const pressureLossLoading3 = (Number(No2press2?.inputData) - Number(No2press6?.inputData)).toFixed(3);
  const pressureLossLoading4 = (Number(No2press2?.inputData) - Number(No2press6?.inputData)).toFixed(3);

  // press data should be displayed with 3 digits
  if(No1cargoTankPress){
    No1cargoTankPress.inputData = Number(No1cargoTankPress.inputData).toFixed(3);
  }
  if(No2cargoTankPress){
    No2cargoTankPress.inputData = Number(No2cargoTankPress.inputData).toFixed(3);
  }

  const checkValue = (value) => {
    if (isNaN(Number(value?.inputData))) {
      return t(localizationKeys.NoLatestData);
    }
    return `${value.inputData} ${value.unit}`;
  };

  return (
    <Grid container className={classes.container}>
      {!isVesselOverview && <Title title={t(localizationKeys.CargoStatus)} />}
      {isLoading ? (
        <CircularProgress style={{ margin: "0 auto" }} />
      ) : (
        <>
          <Grid item size={{xs:5}} className={classes.left}>
            <table className={classes.table} style={{ margin: isVesselOverview ? "auto" : "0 20px 0 auto" }}>
              <thead>
                <tr>
                  <td></td>
                  <td>{`No.1 ${t(localizationKeys.Tank)}`}</td>
                  <td>{`No.2 ${t(localizationKeys.Tank)}`}</td>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{t(localizationKeys.ShieldInnerSurfaceTemp)}</td>
                  <td></td>
                  <td>{checkValue(No2tempOfInner)}</td>
                </tr>
                <tr>
                  <td>{t(localizationKeys.ShieldOuterSurfaceTemp)}</td>
                  <td></td>
                  <td>{checkValue(No2tempOfOuter)}</td>
                </tr>
                <tr>
                  <td>{t(localizationKeys.TankCoverInnerSurfaceTemp)}</td>
                  <td></td>
                  <td>{checkValue(No2tankCoverInnerTemp)}</td>
                </tr>
                <tr>
                  <td>{t(localizationKeys.CargoTankPress)}</td>
                  <td>{checkValue(No1cargoTankPress)}</td>
                  <td>{checkValue(No2cargoTankPress)}</td>
                </tr>
                <tr>
                  <td>{`${t(localizationKeys.CargoTankTemp)}(7740mm)`}</td>
                  <td>{checkValue(No1cargoTankTemp100)}</td>
                  <td>{checkValue(No2cargoTankTemp100)}</td>
                </tr>
                <tr>
                  <td>{`${t(localizationKeys.CargoTankTemp)}(3870mm)`}</td>
                  <td>{checkValue(No1cargoTankTemp50)}</td>
                  <td>{checkValue(No2cargoTankTemp50)}</td>
                </tr>
                <tr>
                  <td>{`${t(localizationKeys.CargoTankTemp)}(3560mm)`}</td>
                  <td>{checkValue(No1cargoTankTemp40)}</td>
                  <td>{checkValue(No2cargoTankTemp40)}</td>
                </tr>
                <tr>
                  <td>{`${t(localizationKeys.CargoTankTemp)}(2200mm)`}</td>
                  <td>{checkValue(No1cargoTankTemp25)}</td>
                  <td>{checkValue(No2cargoTankTemp25)}</td>
                </tr>
                <tr>
                  <td>{t(localizationKeys.BottomFillingLower)}</td>
                  <td>{checkValue(No1bottomFillingLowerTemp)}</td>
                  <td>{checkValue(No2bottomFillingLowerTemp)}</td>
                </tr>
                <tr>
                  <td>{t(localizationKeys.CargoPumpSuction)}</td>
                  <td>{checkValue(No1cargoPumpSuctionTemp)}</td>
                  <td>{checkValue(No2cargoPumpSuctionTemp)}</td>
                  </tr>
              </tbody>
            </table>
          </Grid>
          <Grid item size={{xs:7}} className={classes.right}>
            <table className={classes.table}>
              <tbody>
                <tr>
                  <td>{t(localizationKeys.FlowRate)}</td>
                  <td>{checkValue(flowRate)}</td>
                </tr>
                <tr>
                  <td>{t(localizationKeys.FlowSpeed)}</td>
                  <td>{checkValue({ inputData: flowSpeed, unit: "m/s" })}</td>
                </tr>
                <tr>
                  <td>{t(localizationKeys.Density)}</td>
                  <td>{checkValue(density)}</td>
                </tr>
                <tr>
                  <td>{t(localizationKeys.AMBTemperature)}</td>
                  <td>{checkValue(outsideTemp)}</td>
                </tr>
              </tbody>
            </table>
            <table className={classes.table}>
              <thead>
                <tr>
                  <td></td>
                  <td>{`ΔMPaG`}</td>
                </tr>
                <tr>
                  <td></td>
                  <td>P8 - P1</td>
                  <td>P8 - P2</td>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    {`${t(localizationKeys.PressureLoss)} (${t(localizationKeys.CargoUnloading)})`}
                  </td>
                  <td>{checkValue({ inputData: pressureLossUnloading1, unit: "MPa" })}</td>
                  <td>{checkValue({ inputData: pressureLossUnloading2, unit: "MPa" })}</td>
                </tr>
              </tbody>
              <thead>
                <tr>
                  <td></td>
                  <td>P1 - P5</td>
                  <td>P2 - P5</td>
                  <td>P1 - P6</td>
                  <td>P2 - P6</td>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    {`${t(localizationKeys.PressureLoss)} (${t(localizationKeys.CargoLoading)})`}
                  </td>
                  <td>{checkValue({ inputData: pressureLossLoading1, unit: "MPa" })}</td>
                  <td>{checkValue({ inputData: pressureLossLoading2, unit: "MPa" })}</td>
                  <td>{checkValue({ inputData: pressureLossLoading3, unit: "MPa" })}</td>
                  <td>{checkValue({ inputData: pressureLossLoading4, unit: "MPa" })}</td>
                </tr>
              </tbody>
            </table>
          </Grid>
        </>
      )}
    </Grid>
  );
});

Details.propTypes = {
  data: PropTypes.array,
  isVesselOverview: PropTypes.bool,
};
